// Dashboard del módulo Inventario. Inventario → Resumen → Dashboard.
// Pestañas: Resumen (histórico) + Salud (dinero inmovilizado, dead-stock, aging,
// caducidad, rotación/DIO, GMROI, sobre-stock).
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, ArrowRight, Boxes, Building2, CalendarClock, CheckCircle2, Clock,
  DollarSign, Layers, LayoutDashboard, Loader2, PackagePlus, PackageX, TrendingUp,
  Truck, Warehouse, XCircle, type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { invRpc, money, num, SEMAFORO_CLS, SEMAFORO_LABEL, SortTh, useSort, type Semaforo } from './shared';
import type { Diferencia, TransfConfig, TransitoAbierto } from './Transito';
import { LOCALE } from '@/lib/utils/regional';

interface DashKpis {
  bodegas: number; skus_activos: number; unidades: number; valorizado: number;
  bajo_minimo: number; por_caducar_30: number; movs_7d: number;
}
interface DashData {
  kpis: DashKpis;
  por_bodega: { bodega: string; unidades: number; valor: number }[];
  top_valor: { codigo: string; nombre: string; unidades: number; valor: number }[];
  ultimos_movs: { fecha: string; tipo: string; documento: string | null; codigo: string; nombre: string; unidades: number; bodega: string }[];
}

const TIPO_LABEL: Record<string, string> = {
  recepcion_compra: 'Recepción compra', transferencia_salida: 'Transf. salida', transferencia_entrada: 'Transf. entrada',
  ajuste_positivo: 'Ajuste +', ajuste_negativo: 'Ajuste −', venta: 'Venta', salida_consumo: 'Consumo',
  inventario_inicial: 'Inv. inicial', devolucion_cliente: 'Dev. cliente', devolucion_proveedor: 'Dev. proveedor',
  produccion_consumo: 'Consumo producción', produccion_entrada: 'Entrada producción',
};

// ---------------------------------------------------------------------------
// Salud de inventario — tipos del contrato inv_salud_dashboard(_location)
// ---------------------------------------------------------------------------
interface SaludKpis {
  capital_total: number; capital_muerto: number; capital_muerto_pct: number;
  dio: number; gmroi: number; exceso: number; pct_bajo_minimo: number;
}
interface SaludSucursal {
  location_id: string; nombre: string; valor_total: number; valor_sano: number;
  valor_lento: number; valor_muerto: number; dio: number; rotacion: number;
  gmroi: number; exceso: number; cobertura_prom: number; pct_bajo_min: number;
}
interface SaludAging { bucket: string; valor: number }
interface SaludCaducidad { ventana: string; valor: number; valor_en_riesgo: number }
interface SaludGmroiCategoria { categoria: string; margen_pct: number; rotacion: number; valor: number; gmroi: number }
interface SaludData {
  kpis: SaludKpis;
  por_sucursal: SaludSucursal[];
  aging: SaludAging[];
  caducidad: SaludCaducidad[];
  gmroi_categoria: SaludGmroiCategoria[];
}

const LS_LOCATION_KEY = 'inv_salud_location';
const AGING_ORDEN = ['≤30', '31-60', '61-120', '>120'];
const CADUCIDAD_ORDEN = ['≤30', '≤60', '≤90'];

const nivelGmroi = (v: number): Semaforo => (v < 1 ? 'rojo' : v < 1.5 ? 'ambar' : 'verde');
const nivelMuertoPct = (v: number): Semaforo => (v > 20 ? 'rojo' : v > 10 ? 'ambar' : 'verde');
const nivelBajoMinPct = (v: number): Semaforo => (v > 20 ? 'rojo' : v > 10 ? 'ambar' : 'verde');
const nivelDio = (v: number): Semaforo => (v > 90 ? 'rojo' : v > 60 ? 'ambar' : 'verde');
function nivelFila(s: SaludSucursal, muertoPct: number): Semaforo {
  if (muertoPct > 20 || Number(s.gmroi) < 1) return 'rojo';
  if (muertoPct > 10 || Number(s.pct_bajo_min) > 10) return 'ambar';
  return 'verde';
}
// Deep-link de "Resolver": prioriza reponer > liquidar/mover lentos > rebalancear.
function resolverDestino(s: SaludSucursal, muertoPct: number): { ruta: string; titulo: string } {
  if (Number(s.pct_bajo_min) > 15) return { ruta: '/inventario/reposicion', titulo: 'Reposición' };
  if (muertoPct > 10) return { ruta: '/inventario/abc-productos', titulo: 'Clasificación ABC' };
  return { ruta: '/inventario/transferencias', titulo: 'Transferencias' };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const NIVEL_ICON: Record<Semaforo, LucideIcon> = { verde: CheckCircle2, ambar: AlertTriangle, rojo: XCircle };
function Pill({ nivel }: { nivel: Semaforo }) {
  const Icon = NIVEL_ICON[nivel];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${SEMAFORO_CLS[nivel]}`}>
      <Icon className="w-3 h-3" /> {SEMAFORO_LABEL[nivel]}
    </span>
  );
}

function KpiTile({ icon: Icon, label, valor, sub, nivel, leyenda }:
  { icon: LucideIcon; label: string; valor: string; sub?: string; nivel?: Semaforo; leyenda?: string }) {
  const borderCls = nivel === 'rojo' ? 'border-red-400' : nivel === 'ambar' ? 'border-amber-400' : '';
  return (
    <Card className={borderCls}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="w-4 h-4" /> {label}</div>
        <div className="text-2xl font-semibold font-mono tabular-nums mt-1">{valor}</div>
        <div className="flex items-center justify-between mt-1 gap-2">
          {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
          {nivel && <Pill nivel={nivel} />}
        </div>
        {leyenda && <div className="text-xs text-muted-foreground mt-1">{leyenda}</div>}
      </CardContent>
    </Card>
  );
}

export function DashboardInventario() {
  const [tab, setTab] = useState<'resumen' | 'salud'>('resumen');
  const tabBtn = (t: typeof tab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <LayoutDashboard className="w-5 h-5" /> Dashboard de Inventario
      </h1>

      <div className="flex gap-1 border-b pb-2">
        {tabBtn('resumen', 'Resumen')}
        {tabBtn('salud', 'Salud')}
      </div>

      {tab === 'resumen' ? <ResumenDashboard /> : <SaludDashboard />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Resumen (contenido original, intacto)
// ---------------------------------------------------------------------------
function ResumenDashboard() {
  const { data, isFetching } = useQuery<DashData>({
    queryKey: ['inv-dashboard'],
    queryFn: () => invRpc<DashData>('inv_dashboard'),
    staleTime: 0, refetchOnMount: 'always',
  });

  if (!data) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando dashboard…
      </div>
    );
  }
  const k = data.kpis;

  const tiles = [
    { icon: Warehouse, label: 'Bodegas', valor: num(k.bodegas, 0), sub: 'activas' },
    { icon: Layers, label: 'SKUs activos', valor: num(k.skus_activos, 0), sub: 'productos con stock control' },
    { icon: Boxes, label: 'Unidades en stock', valor: num(k.unidades, 0), sub: `${num(k.movs_7d, 0)} movimientos últimos 7 días` },
    { icon: LayoutDashboard, label: 'Inventario valorizado', valor: money(k.valorizado), sub: 'a costo promedio' },
    { icon: AlertTriangle, label: 'Bajo mínimo', valor: num(k.bajo_minimo, 0), sub: 'productos a reponer', alerta: k.bajo_minimo > 0 },
    { icon: CalendarClock, label: 'Por caducar (30 días)', valor: num(k.por_caducar_30, 0), sub: 'lotes próximos a vencer', alerta: k.por_caducar_30 > 0 },
  ];

  return (
    <div className="space-y-4">
      {isFetching && (
        <div className="flex justify-end"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <Card key={t.label} className={t.alerta ? 'border-amber-400' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><t.icon className="w-4 h-4" /> {t.label}</div>
              <div className={`text-2xl font-semibold font-mono tabular-nums mt-1 ${t.alerta ? 'text-amber-600 dark:text-amber-400' : ''}`}>{t.valor}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TransferenciasPorRecibir />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Valor por bodega</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Bodega</th>
                <th className="py-2 pr-3 text-right">Unidades</th><th className="py-2 pr-3 text-right">Valor</th><th className="py-2 pr-3 text-right">%</th>
              </tr></thead>
              <tbody>
                {data.por_bodega.map((b, i) => (
                  <tr key={b.bodega} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{b.bodega}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(b.unidades, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(b.valor)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">
                      {k.valorizado > 0 ? num((Number(b.valor) / Number(k.valorizado)) * 100, 1) + '%' : '—'}
                    </td>
                  </tr>
                ))}
                {data.por_bodega.length > 0 && (
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={2}>Totales ({data.por_bodega.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(data.por_bodega.reduce((a, b) => a + Number(b.unidades), 0), 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.por_bodega.reduce((a, b) => a + Number(b.valor), 0))}</td>
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 productos por valor</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Producto</th>
                <th className="py-2 pr-3 text-right">Unidades</th><th className="py-2 pr-3 text-right">Valor</th>
              </tr></thead>
              <tbody>
                {data.top_valor.map((p, i) => (
                  <tr key={p.codigo} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3"><span className="font-mono text-xs">{p.codigo}</span> {p.nombre}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(p.unidades, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(p.valor)}</td>
                  </tr>
                ))}
                {!data.top_valor.length && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin existencias</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Últimos movimientos</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3">Documento</th><th className="py-2 pr-3">Producto</th>
              <th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3 text-right">Unidades</th>
            </tr></thead>
            <tbody>
              {data.ultimos_movs.map((m, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{m.fecha}</td>
                  <td className="py-2 pr-3">{TIPO_LABEL[m.tipo] ?? m.tipo}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.documento ?? '—'}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs">{m.codigo}</span> {m.nombre}</td>
                  <td className="py-2 pr-3">{m.bodega}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${Number(m.unidades) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{num(m.unidades, 2)}</td>
                </tr>
              ))}
              {!data.ultimos_movs.length && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin movimientos</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Salud (nueva) — dinero inmovilizado, dead-stock, aging, caducidad,
// rotación/DIO, GMROI, sobre-stock. Gráficos en CSS puro (sin librería nueva).
// ---------------------------------------------------------------------------
function SaludDashboard() {
  const ir = useIr();
  const [locationId, setLocationId] = useState<string | null>(null);
  const [validado, setValidado] = useState(false);

  const { data, isFetching } = useQuery<SaludData>({
    queryKey: ['inv-salud', locationId],
    queryFn: () => invRpc<SaludData>('inv_salud_dashboard', { _location: locationId }),
    staleTime: 0, refetchOnMount: 'always',
  });

  // Restaura la sucursal guardada SOLO si pertenece a esta empresa/usuario
  // (viene del propio por_sucursal de la respuesta) — anti-fuga multi-tenant.
  useEffect(() => {
    if (validado || !data) return;
    try {
      const guardado = localStorage.getItem(LS_LOCATION_KEY);
      if (guardado && data.por_sucursal.some((s) => s.location_id === guardado)) setLocationId(guardado);
    } catch { /* localStorage no disponible: se ignora */ }
    setValidado(true);
  }, [data, validado]);

  const elegirSucursal = (id: string) => {
    setLocationId(id || null);
    try {
      if (id) localStorage.setItem(LS_LOCATION_KEY, id);
      else localStorage.removeItem(LS_LOCATION_KEY);
    } catch { /* best-effort */ }
  };

  const getVal = useCallback((r: SaludSucursal, key: string): string | number | null => {
    const total = Number(r.valor_total) || 0;
    switch (key) {
      case 'sucursal': return r.nombre;
      case 'capital': return Number(r.valor_total);
      case 'muerto': return Number(r.valor_muerto);
      case 'muerto_pct': return total > 0 ? (Number(r.valor_muerto) / total) * 100 : 0;
      case 'dio': return Number(r.dio);
      case 'gmroi': return Number(r.gmroi);
      case 'cobertura': return Number(r.cobertura_prom);
      case 'bajo_min': return Number(r.pct_bajo_min);
      default: return null;
    }
  }, []);
  const sort = useSort(data?.por_sucursal ?? [], getVal);

  if (!data) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando salud de inventario…
      </div>
    );
  }
  const k = data.kpis;
  const porSucursalOrdenado = [...data.por_sucursal].sort((a, b) => Number(b.valor_total) - Number(a.valor_total));
  const agingOrdenado = AGING_ORDEN.map((b) => data.aging.find((a) => a.bucket === b) ?? { bucket: b, valor: 0 });
  const maxAging = Math.max(1, ...agingOrdenado.map((a) => Number(a.valor)));
  const caducidadOrdenada = CADUCIDAD_ORDEN.map((v) => data.caducidad.find((c) => c.ventana === v) ?? { ventana: v, valor: 0, valor_en_riesgo: 0 });

  return (
    <div className="space-y-4">
      {/* Selector Consolidado / Sucursal */}
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <select
          className="rounded-md border border-input bg-background px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={locationId ?? ''}
          onChange={(e) => elegirSucursal(e.target.value)}
        >
          <option value="">Consolidado (todas las sucursales)</option>
          {data.por_sucursal.map((s) => <option key={s.location_id} value={s.location_id}>{s.nombre}</option>)}
        </select>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Tiles con semáforo */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile icon={DollarSign} label="Capital inmovilizado" valor={money(k.capital_total)} sub="Total en inventario a costo" leyenda="Valor total del inventario en stock, a costo." />
        <KpiTile icon={PackageX} label="Dead / lento" valor={money(k.capital_muerto)} sub={`${num(k.capital_muerto_pct, 1)}% del capital`} nivel={nivelMuertoPct(k.capital_muerto_pct)} leyenda="Capital en productos sin rotación (clase D o marcados lentos)." />
        <KpiTile icon={Clock} label="DIO" valor={`${num(k.dio, 0)} días`} sub="Días de inventario" nivel={nivelDio(k.dio)} leyenda="Días que tarda en agotarse el inventario al ritmo de venta actual." />
        <KpiTile icon={TrendingUp} label="GMROI" valor={num(k.gmroi, 2)} sub="Retorno bruto s/ inventario" nivel={nivelGmroi(k.gmroi)} leyenda="Utilidad bruta por cada $1 invertido en stock (>2 sano · <1 pierde)." />
        <KpiTile icon={AlertTriangle} label="Bajo mínimo" valor={`${num(k.pct_bajo_minimo, 1)}%`} sub="de los SKU activos" nivel={nivelBajoMinPct(k.pct_bajo_minimo)} leyenda="SKU cuyo stock cayó a su punto de reposición." />
        <KpiTile icon={PackagePlus} label="Exceso" valor={money(k.exceso)} sub="Sobre el máximo sugerido" leyenda="Valor por encima del máximo sugerido (stock que sobra)." />
      </div>

      {/* Barras apiladas sano/lento/muerto por sucursal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Capital por sucursal — sano / lento / muerto</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Reparto del capital de cada local entre stock sano, lento y muerto.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {porSucursalOrdenado.map((s) => {
            const total = Number(s.valor_total) || 0;
            const pSano = total > 0 ? (Number(s.valor_sano) / total) * 100 : 0;
            const pLento = total > 0 ? (Number(s.valor_lento) / total) * 100 : 0;
            const pMuerto = total > 0 ? (Number(s.valor_muerto) / total) * 100 : 0;
            return (
              <div key={s.location_id}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{s.nombre}</span>
                  <span className="text-muted-foreground font-mono tabular-nums">{money(total)}</span>
                </div>
                <div className="flex h-3 w-full overflow-hidden rounded bg-muted">
                  <div className="bg-emerald-500" style={{ width: `${pSano}%` }} title={`Sano: ${money(s.valor_sano)}`} />
                  <div className="bg-amber-500" style={{ width: `${pLento}%` }} title={`Lento: ${money(s.valor_lento)}`} />
                  <div className="bg-red-500" style={{ width: `${pMuerto}%` }} title={`Muerto: ${money(s.valor_muerto)}`} />
                </div>
              </div>
            );
          })}
          {!porSucursalOrdenado.length && <p className="text-sm text-muted-foreground">Sin datos por sucursal</p>}
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Sano</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Lento</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Muerto</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Aging */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Antigüedad del inventario (aging)</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Antigüedad del inventario según sus días de cobertura.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {agingOrdenado.map((a) => (
              <div key={a.bucket} className="flex items-center gap-2 text-sm">
                <span className="w-16 shrink-0 text-xs text-muted-foreground">{a.bucket} días</span>
                <div className="flex-1 h-3 rounded bg-muted overflow-hidden">
                  <div className="h-full bg-sky-500" style={{ width: `${(Number(a.valor) / maxAging) * 100}%` }} />
                </div>
                <span className="w-24 shrink-0 text-right font-mono tabular-nums text-xs">{money(a.valor)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Caducidad */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ventanas de caducidad</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Valor que vence pronto; en rojo, lo que no rotará antes de caducar.</p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Ventana</th><th className="py-2 pr-3 text-right">Valor</th><th className="py-2 pr-3 text-right">En riesgo</th>
              </tr></thead>
              <tbody>
                {caducidadOrdenada.map((c) => (
                  <tr key={c.ventana} className="border-b last:border-0">
                    <td className="py-2 pr-3">{c.ventana} días</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.valor)}</td>
                    <td className={`py-2 pr-3 text-right font-mono tabular-nums ${Number(c.valor_en_riesgo) > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>
                      {money(c.valor_en_riesgo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Tabla Comparar (ordenable) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Comparar sucursales</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Ranking de sucursales: ordena por cualquier columna para ver la peor.</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="sucursal" sort={sort}>Sucursal</SortTh>
              <SortTh col="capital" sort={sort} align="right">Capital $</SortTh>
              <SortTh col="muerto" sort={sort} align="right">$ muerto</SortTh>
              <SortTh col="muerto_pct" sort={sort} align="right">% muerto</SortTh>
              <SortTh col="dio" sort={sort} align="right">DIO</SortTh>
              <SortTh col="gmroi" sort={sort} align="right">GMROI</SortTh>
              <SortTh col="cobertura" sort={sort} align="right">Cobertura</SortTh>
              <SortTh col="bajo_min" sort={sort} align="right">% bajo mín.</SortTh>
              <th className="py-2 pr-3 text-center">Estado</th>
              <th className="py-2 pr-3" />
            </tr></thead>
            <tbody>
              {sort.sorted.map((s, i) => {
                const total = Number(s.valor_total) || 0;
                const muertoPct = total > 0 ? (Number(s.valor_muerto) / total) * 100 : 0;
                const nivel = nivelFila(s, muertoPct);
                const destino = resolverDestino(s, muertoPct);
                return (
                  <tr key={s.location_id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{s.nombre}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(s.valor_total)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(s.valor_muerto)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(muertoPct, 1)}%</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(s.dio, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(s.gmroi, 2)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(s.cobertura_prom, 0)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(s.pct_bajo_min, 1)}%</td>
                    <td className="py-2 pr-3 text-center"><Pill nivel={nivel} /></td>
                    <td className="py-2 pr-3 text-right">
                      <button
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                        onClick={() => ir(destino.ruta, destino.titulo)}
                        title={`Ir a ${destino.titulo}`}
                      >
                        Resolver <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {sort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={2}>Totales ({sort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sort.sorted.reduce((a, s) => a + Number(s.valor_total), 0))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sort.sorted.reduce((a, s) => a + Number(s.valor_muerto), 0))}</td>
                  <td colSpan={7} />
                </tr>
              )}
              {!sort.sorted.length && <tr><td colSpan={11} className="py-6 text-center text-muted-foreground">Sin datos por sucursal</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* GMROI por categoría */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">GMROI por categoría</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Rentabilidad del inventario por categoría de producto.</p>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Categoría</th>
              <th className="py-2 pr-3 text-right">Margen %</th><th className="py-2 pr-3 text-right">Rotación</th>
              <th className="py-2 pr-3 text-right">Valor</th><th className="py-2 pr-3 text-right">GMROI</th>
            </tr></thead>
            <tbody>
              {data.gmroi_categoria.map((c, i) => (
                <tr key={c.categoria} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{c.categoria}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(c.margen_pct, 1)}%</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(c.rotacion, 2)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.valor)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${Number(c.gmroi) < 1 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}`}>{num(c.gmroi, 2)}</td>
                </tr>
              ))}
              {!data.gmroi_categoria.length && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin datos de categorías</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transferencias por recibir + faltantes sin resolver (inv_transitos_abiertos,
// ya acotado a las bodegas donde el usuario puede recibir). Es el limbo del
// tránsito: lo que salió y no está en ninguna bodega.
// ---------------------------------------------------------------------------
function TransferenciasPorRecibir() {
  const ir = useIr();
  const { data } = useQuery<{ abiertos: TransitoAbierto[]; diferencias_pendientes: Diferencia[] }>({
    queryKey: ['inv-transitos-abiertos'],
    queryFn: () => invRpc('inv_transitos_abiertos', {}),
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 60000,
  });
  const { data: cfg } = useQuery<TransfConfig>({
    queryKey: ['inv-transf-config'], queryFn: () => invRpc<TransfConfig>('inv_transf_config_get', {}),
  });
  const abiertos = data?.abiertos ?? [];
  const sort = useSort(abiertos, (t, c) => {
    switch (c) {
      case 'codigo': return t.codigo;
      case 'ruta': return `${t.origen} ${t.destino}`;
      case 'enviada': return t.enviada_at;
      case 'dias': return Number(t.dias_transito);
      case 'valor': return Number(t.valor_pendiente);
      default: return null;
    }
  }, 'dias', 'desc');
  if (!data) return null;

  const bloqueo = cfg?.bloqueo_caja_dias ?? null;
  const atrasadas = abiertos.filter((t) => t.atrasada).length;
  const valorTransito = abiertos.reduce((a, t) => a + Number(t.valor_pendiente), 0);
  const difs = data.diferencias_pendientes ?? [];
  const valorDifs = difs.reduce((a, d) => a + Number(d.valor), 0);
  const difsTransf = new Set(difs.map((d) => d.transferencia_id)).size;
  const fecha = (v: string) => new Date(v).toLocaleDateString(LOCALE, { day: '2-digit', month: 'short' });

  return (
    <Card className={atrasadas > 0 || difs.length > 0 ? 'border-amber-400' : ''}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Truck className="w-4 h-4" /> Transferencias por recibir</CardTitle>
        <button className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          onClick={() => ir('/inventario/transferencias', 'Transferencias')}>Ir a recibir <ArrowRight className="w-3 h-3" /></button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-muted-foreground">En tránsito</div>
            <div className="text-xl font-semibold font-mono tabular-nums">{abiertos.length}</div></div>
          <div><div className="text-xs text-muted-foreground">Atrasadas</div>
            <div className={`text-xl font-semibold font-mono tabular-nums ${atrasadas ? 'text-red-600 dark:text-red-400' : ''}`}>{atrasadas}</div></div>
          <div><div className="text-xs text-muted-foreground">Valor sin recibir</div>
            <div className="text-xl font-semibold font-mono tabular-nums">{money(valorTransito)}</div></div>
          <div><div className="text-xs text-muted-foreground">Faltantes sin resolver</div>
            <div className={`text-xl font-semibold font-mono tabular-nums ${difs.length ? 'text-amber-600 dark:text-amber-400' : ''}`}>{money(valorDifs)}</div>
            {difs.length > 0 && <div className="text-xs text-muted-foreground">{difs.length} línea(s) en {difsTransf} transferencia(s)</div>}</div>
        </div>

        {abiertos.length === 0 ? (
          <p className="py-3 text-center text-sm text-muted-foreground">No hay transferencias en camino hacia tus bodegas.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="w-10 py-2 pr-3 text-right">N°</th>
                <SortTh col="codigo" sort={sort}>Transferencia</SortTh>
                <SortTh col="ruta" sort={sort}>Origen → destino</SortTh>
                <SortTh col="enviada" sort={sort}>Enviada</SortTh>
                <SortTh col="dias" sort={sort} align="right">Días</SortTh>
                <SortTh col="valor" sort={sort} align="right">Valor pendiente</SortTh>
                <th className="py-2 pr-3">Situación</th>
              </tr></thead>
              <tbody>
                {sort.sorted.map((t, i) => {
                  const bloquea = bloqueo != null && Number(t.dias_transito) > bloqueo;
                  return (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs">{t.codigo}</td>
                      <td className="py-1.5 pr-3">{t.origen} → {t.destino}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{fecha(t.enviada_at)}</td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{t.dias_transito}</td>
                      <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(Number(t.valor_pendiente))}</td>
                      <td className="py-1.5 pr-3 text-xs">
                        {bloquea
                          ? <span className="rounded bg-red-600 px-2 py-0.5 font-semibold text-white">bloquea la caja</span>
                          : t.atrasada
                            ? <span className="rounded bg-red-100 px-2 py-0.5 text-red-700 dark:bg-red-900/30 dark:text-red-300">atrasada</span>
                            : bloqueo != null
                              ? <span className="text-amber-700 dark:text-amber-400">bloquea la caja en {bloqueo - Number(t.dias_transito) + 1} día(s)</span>
                              : <span className="text-muted-foreground">en camino</span>}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 bg-muted/30 font-semibold">
                  <td className="py-2 pr-3" colSpan={5}>Totales ({abiertos.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(valorTransito)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
